import React, { useState } from 'react';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import TrabajoCard from './TrabajoCard';

// Mon–Sat only (no Sunday)
const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const getLoadColor = (piezas) => {
  if (piezas <= 10) return { dot: '🟢', cls: 'text-green-400' };
  if (piezas <= 20) return { dot: '🟠', cls: 'text-orange-400' };
  return { dot: '🔴', cls: 'text-red-400' };
};

// ── Sortable wrapper for each card ────────────────────────────────────────────
const SortableCard = ({ trabajo, isOpen, onToggle, ...cardProps }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: trabajo?.id,
    data: { type: 'trabajo', trabajo, fromDate: cardProps?.fromDate },
  });

  const style = {
    transform: CSS?.Transform?.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 999 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <TrabajoCard
        trabajo={trabajo}
        isOpen={isOpen}
        onToggle={onToggle}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
        {...cardProps}
      />
    </div>
  );
};

// ── Droppable column (used by the parent DndContext) ──────────────────────────
const DayColumn = ({
  dayIndex,
  date,
  trabajos = [],
  isToday = false,
  onDrop,
  onEstadoChange,
  onMover,
  onEditar,
  onEliminar,
  onAjustarPiezas,
  onEmpezarPieza,
  onVerRegistro,
  onEmpezar,
  onIncidencia,
  onMarcarRecibido,
  onAddTrabajo,
  onReorder,
  // Passed from parent DndContext to know which card is being dragged over this column
  activeId,
  overId,
}) => {
  const [openCardId, setOpenCardId] = useState(null);

  const handleToggleCard = (id) => {
    setOpenCardId(prev => prev === id ? null : id);
  };

  // Make the column itself a droppable target (for cross-day drops)
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `col-${date || 'unscheduled'}`,
    data: { type: 'column', date },
  });

  const totalPiezas = trabajos?.reduce((sum, t) => sum + (t?.cantidad_piezas || 1), 0);
  const loadInfo = getLoadColor(totalPiezas);
  const dateLabel = date ? new Date(date + 'T12:00:00')?.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '';

  // Determine if a foreign card is hovering over this column
  const isDragOver = isOver;

  return (
    <div
      ref={setDropRef}
      className={`flex flex-col min-w-[155px] flex-1 rounded-xl border transition-all duration-150 ${
        isDragOver
          ? 'border-[#D6EF4E]/60 bg-[#D6EF4E]/8 ring-1 ring-[#D6EF4E]/30'
          : isToday
          ? 'border-[#D6EF4E]/20 bg-[#D6EF4E]/3'
          : 'border-white/8 bg-white/3'
      }`}
    >
      {/* Column header */}
      <div className={`px-3 py-2.5 border-b ${isToday ? 'border-[#D6EF4E]/15' : 'border-white/8'}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className={`font-bold font-oranienbaum text-sm ${isToday ? 'text-[#D6EF4E]' : 'text-white/80'}`}>
              {DIAS?.[dayIndex]}
            </div>
            {dateLabel && (
              <div className="text-white/35 text-[10px] font-oranienbaum">{dateLabel}</div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <span className={`text-xs font-oranienbaum font-semibold ${loadInfo?.cls}`}>{totalPiezas}</span>
            <span className="text-xs">{loadInfo?.dot}</span>
          </div>
        </div>
        {totalPiezas > 0 && (
          <div className="text-[10px] text-white/30 font-oranienbaum mt-0.5">{totalPiezas} piezas</div>
        )}
        {/* Drop hint when dragging over */}
        {isDragOver && (
          <div className="mt-1.5 text-[10px] text-[#D6EF4E]/70 font-oranienbaum text-center animate-pulse">
            ↓ Soltar aquí
          </div>
        )}
      </div>

      {/* Cards with dnd-kit sortable — no DndContext here, it lives in the parent */}
      <div className="flex-1 p-2 space-y-1 min-h-[120px]">
        <SortableContext
          items={trabajos?.map(t => t?.id)}
          strategy={verticalListSortingStrategy}
        >
          {trabajos?.map((trabajo) => (
            <SortableCard
              key={trabajo?.id}
              trabajo={trabajo}
              isOpen={openCardId === trabajo?.id}
              onToggle={() => handleToggleCard(trabajo?.id)}
              onEstadoChange={onEstadoChange}
              onMover={onMover}
              onEditar={onEditar}
              onEliminar={onEliminar}
              onAjustarPiezas={onAjustarPiezas}
              onEmpezarPieza={onEmpezarPieza}
              onVerRegistro={onVerRegistro}
              onEmpezar={onEmpezar}
              onIncidencia={onIncidencia}
              onMarcarRecibido={onMarcarRecibido}
              fromDate={date}
            />
          ))}
        </SortableContext>

        {/* Empty drop zone hint */}
        {isDragOver && trabajos?.length === 0 && (
          <div className="border-2 border-dashed border-[#D6EF4E]/40 rounded-lg h-16 flex items-center justify-center">
            <span className="text-[#D6EF4E]/60 text-xs font-oranienbaum">Soltar aquí</span>
          </div>
        )}

        <button
          onClick={() => onAddTrabajo?.(date)}
          className="w-full py-1.5 rounded-lg border border-dashed border-white/10 text-white/25 hover:border-white/25 hover:text-white/50 transition-colors text-xs font-oranienbaum"
        >
          + Añadir
        </button>
      </div>
    </div>
  );
};

export default DayColumn;
